import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { hospitalsAPI, subscriptionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Search,
  Edit2,
  Trash2,
  Users,
  Mail,
  Phone,
  MapPin,
} from 'lucide-react';

export const HospitalsPage = () => {
  const { user } = useAuth();
  const [hospitals, setHospitals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    slug: '',
    dns_configured: false,
    employee_id_prefix: '',
    address: '',
    phone: '',
    email: '',
    subscription_plan_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const isSuperAdmin = user?.is_super_admin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hospitalsRes, plansRes] = await Promise.all([
        hospitalsAPI.list(),
        subscriptionsAPI.listPlans(),
      ]);
      setHospitals(hospitalsRes.data);
      setPlans(plansRes.data);
    } catch (err) {
      toast.error('Failed to load hospitals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await hospitalsAPI.create(formData);
      toast.success('Hospital created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create hospital');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await hospitalsAPI.update(selectedHospital.id, formData);
      toast.success('Hospital updated successfully');
      setIsEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update hospital');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (hospital) => {
    if (!window.confirm(`Are you sure you want to deactivate ${hospital.name}?`)) return;
    
    try {
      await hospitalsAPI.delete(hospital.id);
      toast.success('Hospital deactivated successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate hospital');
    }
  };

  const openEditDialog = (hospital) => {
    setSelectedHospital(hospital);
    setFormData({
      name: hospital.name,
      code: hospital.code,
      slug: hospital.slug || '',
      dns_configured: !!hospital.dns_configured,
      employee_id_prefix: hospital.employee_id_prefix || '',
      address: hospital.address || '',
      phone: hospital.phone || '',
      email: hospital.email || '',
      subscription_plan_id: hospital.subscription_plan_id || '',
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      slug: '',
      dns_configured: false,
      employee_id_prefix: '',
      address: '',
      phone: '',
      email: '',
      subscription_plan_id: '',
    });
    setSelectedHospital(null);
  };

  const filteredHospitals = hospitals.filter(
    (h) =>
      h.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      h.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Access denied. Super admin only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hospitals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Hospitals
          </h1>
          <p className="mt-1 text-slate-600">Manage registered hospitals on the platform</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-hospital-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Hospital
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Hospital</DialogTitle>
              <DialogDescription>Add a new hospital to the platform</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Hospital Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="hospital-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">Hospital Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      placeholder="e.g., HOSP001"
                      data-testid="hospital-code-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Slug (for subdomain) </Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="e.g., prashanti  →  prashanti.klickniramaya.com"
                    data-testid="hospital-slug-input"
                  />
                  <p className="text-[11px] text-slate-500">Lowercase letters / numbers / hyphens only. Drives the tenant subdomain URL.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="hospital-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="hospital-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="hospital-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plan">Subscription Plan</Label>
                  <Select
                    value={formData.subscription_plan_id}
                    onValueChange={(value) => setFormData({ ...formData, subscription_plan_id: value })}
                  >
                    <SelectTrigger data-testid="hospital-plan-select">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name} - ₹{plan.price}/mo
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="hospital-submit-btn">
                  {submitting ? 'Creating...' : 'Create Hospital'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search hospitals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="hospital-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Hospitals Table */}
      <Card>
        <CardContent className="p-0">
          <div className="data-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Hospital</TableHead>
                  <TableHead className="font-semibold">Contact</TableHead>
                  <TableHead className="font-semibold">Plan</TableHead>
                  <TableHead className="font-semibold">Users</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHospitals.map((hospital) => (
                  <TableRow key={hospital.id} data-testid={`hospital-row-${hospital.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{hospital.name}</p>
                          <p className="text-sm text-slate-500">{hospital.code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {hospital.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="h-3 w-3" />
                            {hospital.email}
                          </div>
                        )}
                        {hospital.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="h-3 w-3" />
                            {hospital.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {hospital.subscription_plan?.name || 'No Plan'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-slate-400" />
                        <span>{hospital.user_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={hospital.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {hospital.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(hospital)}
                          data-testid={`edit-hospital-${hospital.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(hospital)}
                          data-testid={`delete-hospital-${hospital.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredHospitals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No hospitals found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Hospital</DialogTitle>
            <DialogDescription>Update hospital details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Hospital Name *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-code">Hospital Code</Label>
                  <Input id="edit-code" value={formData.code} disabled className="bg-slate-50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-slug">URL Slug (for subdomain)</Label>
                <Input
                  id="edit-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="e.g., prashanti  →  prashanti.klickniramaya.com"
                  data-testid="hospital-edit-slug-input"
                />
                <p className="text-[11px] text-slate-500">Lowercase letters / numbers / hyphens only. Determines the tenant subdomain URL.</p>
              </div>
              <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-3 bg-slate-50/60">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-dns" className="text-sm font-medium">DNS / Subdomain Live</Label>
                  <p className="text-[11px] text-slate-500 max-w-xs">
                    Turn ON once <code className="text-[10px]">{formData.slug || '<slug>'}.&lt;your-root-domain&gt;</code> is actually
                    DNS-configured. Until then, the root-domain dropdown will show
                    a &quot;workspace not configured&quot; screen for this hospital instead of redirecting.
                  </p>
                </div>
                <Switch
                  id="edit-dns"
                  checked={!!formData.dns_configured}
                  onCheckedChange={(v) => setFormData({ ...formData, dns_configured: !!v })}
                  data-testid="hospital-edit-dns-toggle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-prefix">Employee ID Prefix</Label>
                <Input
                  id="edit-prefix"
                  value={formData.employee_id_prefix || ''}
                  onChange={(e) => setFormData({ ...formData, employee_id_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) })}
                  placeholder="e.g., PMSH → PMSH-DOC-P-1000"
                  className="font-mono uppercase"
                  data-testid="hospital-edit-prefix-input"
                />
                <p className="text-[11px] text-slate-500">2-6 uppercase letters/digits. Drives the Employee ID format <code>&lt;PREFIX&gt;-&lt;DEPT&gt;-&lt;P|C|T&gt;-&lt;SERIAL&gt;</code>. If blank, the first 4 chars of <code>code</code> are used.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-plan">Subscription Plan</Label>
                <Select
                  value={formData.subscription_plan_id}
                  onValueChange={(value) => setFormData({ ...formData, subscription_plan_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - ₹{plan.price}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HospitalsPage;
